import localforage from "localforage";

/**
 * Adopts a `?clientId=` handed over by the DealerFlow360 shell.
 *
 * DF360 stamps the active client onto the URL when it opens a legacy module, because this app is
 * a different origin and cannot see anything DF360 stored. Without it, a super user who switched
 * to client B in DF360 and then opened LOS would land on whatever client LOS was last used with —
 * client A — while the shell said B.
 *
 * ⚠️ Near-identical copies live in crm-client and wwda.customer-center. They are separately built
 * SPAs with no shared workspace package, so the duplication is deliberate — keep them in step.
 *
 * ⚠️ MUST be awaited BEFORE authentication starts and before the first API call. `localforage` is
 * asynchronous (IndexedDB), and every HTTP helper in this app reads `clientId` out of it per
 * request. Doing this from a React effect — as this used to — lets the first requests fire while
 * the write is still in flight, so they go out under the previous client. That is a race that
 * fails intermittently and looks exactly like the bug it was meant to fix.
 */
export async function adoptClientIdHandoff(): Promise<void> {
  const requested = new URLSearchParams(window.location.search).get("clientId");
  if (!requested) return;

  // The value lands in a query string on every subsequent API call, so it is validated as an
  // opaque client code before it is trusted. A malformed one is IGNORED rather than reported:
  // this is a machine-to-machine hand-off, not user input, and falling back to the user's
  // assigned client is the safe outcome.
  if (!/^[A-Za-z0-9._-]{1,64}$/.test(requested)) {
    console.warn("Ignoring a malformed clientId hand-off.");
    return;
  }

  try {
    await localforage.setItem("clientId", requested);
  } catch (error) {
    // Storage unavailable (private mode): the app falls back to the user's assigned client,
    // which is wrong but not dangerous — better than blocking sign-in.
    console.error("Failed to persist the DealerFlow360 client hand-off:", error);
    return;
  }

  // Drop the parameter so a later reload or shared link does not re-pin a stale client.
  const url = new URL(window.location.href);
  url.searchParams.delete("clientId");
  window.history.replaceState({}, "", url.toString());
}
